<script lang="ts" setup>
import type { AutocompleteListModel as LabelListModel } from '../../../pages/store/catalog/labels/_runtime/services/LabelsService';
import LabelsService from '../../../pages/store/catalog/labels/_runtime/services/LabelsService';

defineOptions({
    name: 'WidgetTaggedProductsSettings'
});

interface FormSettings
{
    settings: {
        limit: number;
        format: string;
        isSale: boolean;
        labelsIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    limit: 1,
    format: 'slider',
    isSale: false,
    labelsIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const labelId = ref<number>(null);
const labels = computedAsync(async () =>
{
    try
    {
        if (form.value.settings.labelsIds.length === 0)
            return [];

        return await LabelsService.getListByIds(form.value.settings.labelsIds);
    }
    catch (ex)
    {
        return [];
    }
});

const displayOptions: Option<number>[] = [
    { value: 1, text: $t('[[[1]]]') },
    { value: 2, text: $t('[[[2]]]') },
    { value: 3, text: $t('[[[3]]]') },
    { value: 4, text: $t('[[[4]]]') },
    { value: 5, text: $t('[[[5]]]') },
    { value: 6, text: $t('[[[6]]]') },
    { value: 7, text: $t('[[[7]]]') },
    { value: 8, text: $t('[[[8]]]') },
    { value: 9, text: $t('[[[9]]]') },
    { value: 10, text: $t('[[[10]]]') }
];

const formatOptions: Option[] = [
    { value: 'slider', text: $t('[[[slider]]]') },
    { value: 'grid', text: $t('[[[grid]]]') }
];

const fetchLabel = async (id: number): Promise<LabelListModel> =>
{
    const response = await LabelsService.fetchAutocompleteModel(id);

    return response.first() ?? null;
};

const searchLabel = async (query: string, limit: number): Promise<LabelListModel[]> =>
{
    return await LabelsService.getAutocompleteList(query, limit, form.value.settings.labelsIds);
};

const addLabel = async (): Promise<void> =>
{
    if (labelId.value == null)
        return;

    form.value.settings.labelsIds.push(labelId.value);
    labelId.value = null;
};

const removeLabel = async (id: number): Promise<void> =>
{
    form.value.settings.labelsIds = form.value.settings.labelsIds.filter(p => p !== id);
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            limit: schema.number().required($t('[[[Wybranie limitu jest wymagane]]]')).typeError($t('[[[Wybranie limitu jest wymagane]]]')),
            format: schema.string().required($t('[[[Wybranie formatu jest wymagane]]]')).typeError($t('[[[Wybranie formatu jest wymagane]]]')),
            labelsIds: schema.array(schema.number().required($t('[[[Wybranie etykiety produktu jest wymagane]]]'))).min(1, $t('[[[Wybranie etykiety produktu jest wymagane]]]')).typeError($t('[[[Wybranie etykiety produktu jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings.limit, () =>
{
    form.value.$errors.clear('settings.limit');
});

watch(() => form.value.settings.format, () =>
{
    form.value.$errors.clear('settings.format');
});

watch(() => form.value.settings.labelsIds, () =>
{
    form.value.$errors.clear('settings.labelsIds');
}, { deep: true });

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <IdeoFormGroup :label="$t('[[[Liczba wyświetlanych produktów]]]')" :invalid-feedback="form.$errors.first('settings.limit')" :state="form.$errors.state('settings.limit')">
            <IdeoFormSelect v-model="form.settings.limit" :options="displayOptions" />
        </IdeoFormGroup>
        <IdeoFormGroup :label="$t('[[[Szablon]]]')" :invalid-feedback="form.$errors.first('settings.format')" :state="form.$errors.state('settings.format')">
            <IdeoFormSelect v-model="form.settings.format" :options="formatOptions" />
        </IdeoFormGroup>
        <div class="pt-2 pb-3">
            <IdeoFormCheckbox v-model="form.settings.isSale">
                {{ $t('[[[Produkty w promocji]]]') }}
            </IdeoFormCheckbox>
        </div>

        <IdeoFormGroup :label="$t('[[[Wybierz etykiety produktów]]]')" :invalid-feedback="form.$errors.first('settings.labelsIds')" :state="form.$errors.state('settings.labelsIds')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <IdeoAutocomplete
                        v-model="labelId"
                        name="labelsIds"
                        track-by="id"
                        :placeholder="$t('[[[zacznij pisać aby wyszukać etykietę...]]]')"
                        :fetch="(id: number) => fetchLabel(id)"
                        :search="(query: string, limit: number) => searchLabel(query, limit)"
                        :refresh="true"
                    >
                        <template #selected="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                    </IdeoAutocomplete>
                </div>
                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addLabel()">
                        <i class="fas fa-plus" />
                    </button>
                </div>
            </div>
        </IdeoFormGroup>

        <h5 class="mb-3">
            {{ $t('[[[Etykiety]]]') }}
        </h5>
        <IdeoFormGroup v-if="labels">
            <div class="list-group">
                <div v-for="item in labels" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                    <div class="d-flex align-items-center justify-content-between gap-2 w-100">
                        <div class="d-flex align-items-center gap-2">
                            <i class="fad fa-fw fa-browser text-primary" />
                            <b :title="$t('[[[Nazwa]]]')">
                                {{ item.name }}
                            </b>
                        </div>

                        <div class="d-flex align-items-center">
                            <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-label-${item.id}`" :value="item.id" @confirm="removeLabel(item.id)" />
                            <IdeoButton :id="`btn-delete-label-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń produkt]]]')">
                                <i class="fas fa-trash-alt" />
                            </IdeoButton>
                        </div>
                    </div>
                </div>
                <div v-if="form.settings.labelsIds.length == 0" class="list-group-item list-group-item-light text-center">
                    {{ $t('[[[Brak wybranych etykiet]]]') }}
                </div>
            </div>
        </IdeoFormGroup>
    </IdeoFormGroup>
</template>
